/**
 * VisitingCardModal — renders the agent's personalised visiting card as a
 * SINGLE consolidated card (per print spec at
 * FBL Visiting Card For TeleSales 2.pdf) and exports it as a PNG the agent
 * can share on WhatsApp.
 *
 * Per Shailendra 2026-05-26: agents complained the front+back split confused
 * buyers — WA previews crop one side. One unified image keeps the brand
 * lockup, agent name, contacts, and QR all visible in a single frame.
 *
 * Buyers scanning the QR open a direct WhatsApp chat with the agent, which
 * combats the "online fraud" hesitation before the 5% advance.
 *
 * Data sources (all client-side):
 *   name        ← localStorage 'name'
 *   phoneNumber ← decrypted from localStorage 'phoneNumber'
 *   role        ← localStorage 'setRole' (decrypted) → designation
 *   email       ← info@floridabeautylabs.com (fixed per spec)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, CircularProgress, Box, Typography } from '@material-ui/core';
import { toPng } from 'html-to-image';
import QRCode from 'qrcode';
import axios from 'axios';
import { getDecryptDataFromLocal } from '../../utils/encrypt';

const AI_PATH = process.env.REACT_APP_AI_PATH;

const COMPANY_EMAIL   = 'info@floridabeautylabs.com';
const COMPANY_ADDRESS = 'Florida Beauty Pvt. Ltd.\nPlot No. 31, Okhla Phase III, Okhla Industrial Estate,\nNew Delhi 110020';

// Map admin userType to a printed designation. "Relationship Manager" is the
// public-facing title for regular WA agents (userType 5) — feels like a
// dedicated CAM to the buyer (Shailendra 2026-05-26), not a faceless tele-rep.
const designationForRole = (role) => {
  const r = Number(role);
  if (r === 1)  return 'Founder';
  if (r === 17) return 'Senior Manager';
  if (r === 14) return 'Floor Manager';
  if (r === 13) return 'Team Lead';
  return 'Relationship Manager';
};

const VisitingCardModal = ({ open, onClose }) => {
  const cardRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [exporting, setExporting]   = useState(false);

  // Read agent info from localStorage at mount.
  // Priority for the printed phone:
  //   1. localStorage 'visitingCardPhone' (set when agent edits profile)
  //   2. login phone (decrypted 'phoneNumber')
  // The QR + display always reflect the chosen number, so a customer who scans
  // the card lands directly on the agent's preferred WhatsApp SIM.
  // localStorage is only a first paint — it survives logout on shared PCs and
  // is empty on any browser where the agent never saved the profile, so the
  // effect below re-reads the admin doc each time the modal opens.
  const buildInfo = (vcPhone, name) => {
    const loginPhone = (() => { try { return getDecryptDataFromLocal('phoneNumber') || ''; } catch (e) { return ''; } })();
    const role       = (() => { try { return getDecryptDataFromLocal('setRole') || 5; } catch (e) { return 5; } })();
    const pickPhone  = vcPhone && vcPhone.replace(/[^0-9]/g, '').length >= 10 ? vcPhone : loginPhone;
    // Only strip a leading "91" when it's a real country-code prefix (12 digits
    // total) — a bare 10-digit number starting with 91 (e.g. 9122033233) must
    // be left untouched, or its first 2 digits get lost.
    const rawDigits  = String(pickPhone).replace(/[^0-9]/g, '');
    const clean10    = (rawDigits.length === 12 && rawDigits.startsWith('91')) ? rawDigits.slice(2) : rawDigits.slice(-10);
    return {
      name:        (name || 'Sales Executive').trim().toUpperCase(),
      designation: designationForRole(role).toUpperCase(),
      phoneE164:   '+91-' + clean10,
      phoneDigits: '91' + clean10,
    };
  };
  const [info, setInfo] = useState(() => buildInfo(
    (() => { try { return localStorage.getItem('visitingCardPhone') || ''; } catch (e) { return ''; } })(),
    (() => { try { return localStorage.getItem('name') || ''; } catch (e) { return ''; } })(),
  ));

  useEffect(() => {
    if (!open || !AI_PATH) return;
    const adminId = (() => { try { return localStorage.getItem('adminId') || ''; } catch (e) { return ''; } })();
    if (!adminId) return;
    axios.get(`${AI_PATH}/api/ai/admin/me`, { params: { adminId } })
      .then((res) => {
        const a = res.data?.admin;
        if (!a) return;
        try { localStorage.setItem('visitingCardPhone', a.visitingCardPhone || ''); } catch (e) { /* storage blocked */ }
        setInfo(buildInfo(a.visitingCardPhone || '', a.name || localStorage.getItem('name') || ''));
      })
      .catch((e) => console.warn('[VisitingCard] profile fetch failed:', e.message));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate the QR (points to wa.me/<agent number>) once when modal opens.
  useEffect(() => {
    if (!open) return;
    QRCode.toDataURL(`https://wa.me/${info.phoneDigits}`, {
      margin: 0,
      width: 200,
      color: { dark: '#1B2A47', light: '#FFFFFF' },
    })
      .then(setQrDataUrl)
      .catch((e) => console.warn('[VisitingCard] QR gen failed:', e.message));
  }, [open, info.phoneDigits]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,                 // 2× for retina-sharp PNG
        backgroundColor: '#cccccc',    // matches Manjay's mockup grey
        cacheBust: true,
      });
      const a = document.createElement('a');
      a.href     = dataUrl;
      a.download = `visiting-card-${info.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('[VisitingCard] export failed:', err);
      alert('Download failed: ' + (err.message || 'try again'));
    } finally {
      setExporting(false);
    }
  };

  // Brand palette (locked by Florida Beauty print spec — CMYK 98-79-46-46 navy,
  // 4-5-16-00 cream).
  const NAVY      = '#152A47';
  const CREAM     = '#FCEFD2';
  const SUB       = '#152A47';
  // 5.5 : 3 card aspect — captures cleanly at pixelRatio:2 → 2400×1320 PNG.
  const CARD_W    = 1200;
  const CARD_H    = 660;
  const HEADER_H  = 150;       // navy band that holds the 3 sub-brand lockups
  const CHEVRON_W = 640;       // cream nameplate width

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent style={{ background: '#cccccc', padding: 24 }}>
        <Typography variant="caption" style={{ display: 'block', textAlign: 'center', color: '#666', marginBottom: 12 }}>
          Preview — your visiting card. Click <strong>Download PNG</strong> below to save it, then share on WhatsApp.
        </Typography>

        {/* Card content captured by html-to-image — single consolidated card */}
        <Box ref={cardRef} style={{
          background: '#cccccc',
          padding: 28,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Box style={{
            width: CARD_W, height: CARD_H, background: '#fff', borderRadius: 10,
            position: 'relative', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            fontFamily: 'Georgia, serif',
          }}>
            {/* ── Navy header band with the 3 sub-brand lockups ── */}
            <Box style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H,
              background: NAVY,
              display: 'flex', alignItems: 'center', justifyContent: 'space-around',
              padding: '0 80px',
            }}>
              <Box style={{ textAlign: 'center' }}>
                <Typography style={{ fontSize: 34, fontWeight: 700, color: '#fff', letterSpacing: 0.5, lineHeight: 1, fontFamily: 'Helvetica, Arial, sans-serif' }}>clensta®</Typography>
                <Typography style={{ fontSize: 11, color: '#fff', letterSpacing: 1.5, marginTop: 4, fontFamily: 'Helvetica, Arial, sans-serif' }}>the better way</Typography>
              </Box>
              <Box style={{ textAlign: 'center' }}>
                <Typography style={{ fontSize: 34, fontWeight: 800, color: '#fff', letterSpacing: 0.5, lineHeight: 1, fontFamily: 'Helvetica, Arial, sans-serif' }}>cocowalk</Typography>
                <Typography style={{ fontSize: 10, color: '#fff', letterSpacing: 3, marginTop: 4, fontFamily: 'Helvetica, Arial, sans-serif' }}>PROFESSIONAL</Typography>
              </Box>
              <Box style={{ textAlign: 'center' }}>
                <Typography style={{ fontSize: 34, fontWeight: 700, color: '#fff', letterSpacing: 0.5, lineHeight: 1, fontFamily: 'Helvetica, Arial, sans-serif' }}>keywest</Typography>
                <Typography style={{ fontSize: 10, color: '#fff', letterSpacing: 3, marginTop: 4, fontFamily: 'Helvetica, Arial, sans-serif' }}>PROFESSIONAL</Typography>
              </Box>
            </Box>

            {/* ── Cream nameplate chevron — overlaps navy/white border ── */}
            <Box style={{
              position: 'absolute', top: HEADER_H - 40, left: 0,
              width: CHEVRON_W, padding: '24px 90px 24px 60px',
              background: CREAM,
              clipPath: 'polygon(0 0, 88% 0, 100% 50%, 88% 100%, 0 100%)',
            }}>
              <Typography style={{ fontSize: 32, fontWeight: 800, color: SUB, lineHeight: 1.1, letterSpacing: 1, fontFamily: 'Helvetica, Arial, sans-serif' }}>
                {info.name}
              </Typography>
              <Typography style={{ fontSize: 14, color: SUB, marginTop: 6, letterSpacing: 2.5, textDecoration: 'underline', fontFamily: 'Helvetica, Arial, sans-serif' }}>
                {info.designation}
              </Typography>
            </Box>

            {/* ── Left column — phone / email / address ── */}
            <Box style={{ position: 'absolute', top: 330, left: 60, right: CARD_W - 760, display: 'flex', flexDirection: 'column', gap: 22 }}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', background: NAVY, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>📞</span>
                <Typography style={{ fontSize: 18, color: SUB, fontWeight: 500, fontFamily: 'Helvetica, Arial, sans-serif' }}>{info.phoneE164}</Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', background: NAVY, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✉️</span>
                <Typography style={{ fontSize: 16, color: SUB, fontWeight: 500, fontFamily: 'Helvetica, Arial, sans-serif' }}>{COMPANY_EMAIL}</Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <span style={{ width: 32, height: 32, borderRadius: '50%', background: NAVY, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, marginTop: 2 }}>📍</span>
                <Typography style={{ fontSize: 14, color: SUB, lineHeight: 1.5, whiteSpace: 'pre-line', fontFamily: 'Helvetica, Arial, sans-serif' }}>{COMPANY_ADDRESS}</Typography>
              </Box>
            </Box>

            {/* ── Right column — florida beauty labs mini lockup + QR ── */}
            <Box style={{ position: 'absolute', top: 250, right: 80, width: 220, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Stacked "florida beauty labs" word lockup */}
              <Box style={{ textAlign: 'left', marginBottom: 18 }}>
                <Typography style={{ fontSize: 28, fontWeight: 300, lineHeight: 0.95, color: NAVY, fontFamily: 'Helvetica, Arial, sans-serif' }}>florida</Typography>
                <Typography style={{ fontSize: 28, fontWeight: 800, lineHeight: 0.95, color: NAVY, fontFamily: 'Helvetica, Arial, sans-serif' }}>beauty</Typography>
                <Typography style={{ fontSize: 14, fontWeight: 300, letterSpacing: 6, color: NAVY, marginTop: 2, fontFamily: 'Helvetica, Arial, sans-serif' }}>labs</Typography>
              </Box>
              {qrDataUrl && (
                <img src={qrDataUrl} alt="QR" style={{
                  width: 170, height: 170,
                  border: '6px solid #fff', borderRadius: 4,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }} />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: 16, background: '#f5f5f5' }}>
        <Button onClick={onClose} disabled={exporting}>Close</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          color="primary"
          disabled={exporting || !qrDataUrl}
          startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {exporting ? 'Generating…' : 'Download PNG'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitingCardModal;
